<script setup lang="ts">
import Skeleton from "primevue/skeleton";
import { useTenantTheming } from "@/composables/useTenantTheming.ts";
import type { Props } from "./types.ts";

defineOptions({
  name: "ExcursionConfiguratorSkeleton",
});

withDefaults(defineProps<Props>(), {
  includedCount: 3,
  optionalCount: 2,
});

// Match the loaded configurator's storefront branding so the accent bands don't flash API colors.
const { secondaryColor } = useTenantTheming();
</script>

<template>
  <div class="flex flex-col w-full gap-8" data-testid="excursion-configurator-skeleton">
    <!-- Included section -->
    <div class="shadow-[0px_4px_20px_0px_#00000026]">
      <div
        class="flex w-full py-2 px-4 justify-start items-center"
        :class="secondaryColor ? 'bg-gray-275' : 'bg-blue-400'"
      >
        <Skeleton width="200px" height="24px" />
      </div>
      <div class="flex flex-col gap-4 p-4">
        <Skeleton width="100%" height="20px" />
        <div class="flex gap-4 flex-wrap">
          <div
            v-for="i in includedCount"
            :key="`included-${i}`"
            class="px-2 pb-2 w-[377px] max-w-full"
            data-testid="included-skeleton-item"
          >
            <div class="overflow-hidden shadow-md bg-white">
              <div class="p-4" :class="secondaryColor ? 'bg-gray-275' : 'bg-teal-accent'">
                <Skeleton width="120px" height="14px" />
              </div>
              <Skeleton width="100%" height="212px" borderRadius="0" />
              <div class="flex flex-col gap-3 p-4">
                <Skeleton width="80%" height="20px" />
                <Skeleton width="60%" height="20px" />
                <Skeleton width="50%" height="14px" />
              </div>
              <div class="flex justify-end p-2">
                <Skeleton width="100px" height="16px" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Optional section -->
    <div class="flex flex-col w-full gap-4">
      <div class="flex flex-col w-full gap-2">
        <Skeleton width="240px" height="24px" />
        <Skeleton width="80%" height="16px" />
      </div>
      <div class="flex flex-col gap-4">
        <div
          v-for="i in optionalCount"
          :key="`optional-${i}`"
          class="grid grid-cols-2 shadow-[0px_4px_20px_2px_#0000001A]"
          data-testid="optional-skeleton-item"
        >
          <div class="flex flex-col min-h-0">
            <div class="p-4" :class="secondaryColor ? 'bg-gray-275' : 'bg-teal-accent'">
              <Skeleton width="120px" height="14px" />
            </div>
            <Skeleton width="100%" height="265px" borderRadius="0" />
          </div>
          <div class="flex flex-col p-6 gap-4">
            <div class="flex justify-end">
              <Skeleton width="60px" height="20px" />
            </div>
            <Skeleton width="70%" height="28px" />
            <div class="flex flex-col gap-1">
              <Skeleton width="60%" height="14px" />
              <Skeleton width="40%" height="14px" />
            </div>
            <div class="flex flex-col gap-1">
              <Skeleton width="100%" height="14px" />
              <Skeleton width="100%" height="14px" />
              <Skeleton width="80%" height="14px" />
            </div>
            <div class="flex justify-between mt-auto">
              <Skeleton width="100px" height="20px" />
              <Skeleton width="140px" height="48px" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
